/* ========================================
   KL RETURN-ADD + MEMBER QUICK ADD
   1) After sign-in, ?kl_add=<variant_id> brings the member back to the product.
      If they're signed in now, auto-add that variant and open Reformation's
      cart drawer. Mirrors the theme's own add-to-cart -> render -> open-drawer
      flow (product.js).
   2) Any element with [data-kl-add="<variant_id>"] adds that variant on click,
      via the same flow. These buttons are rendered server-side (the hero's
      buy button).
   Added: 2026-05-26 for DEV-018 buy-flow.
   2026-07-31: shared helpers, [data-kl-add] handler.
   2026-09-10 (DEV-020 Phase 3): the expired-lockbox guard is deleted with the
   drop machinery — nothing is ever unbuyable now.
   ======================================== */

(function () {
  function getSectionInnerHTML(html, selector) {
    var tmp = document.createElement('div');
    tmp.innerHTML = html;
    var el = tmp.querySelector(selector);
    return el ? el.innerHTML : null;
  }

  function closeQuickViewPanels() {
    // WIN-1L it3 (HOD gate, 2026-08-13): the cart drawer was opening BEHIND a
    // still-open quick-view/quick-add panel — the member saw nothing and a
    // re-enabled button invited double-adds. Mirror the theme's own flow
    // (assets/product.js ~856): close the quick-view before the drawer opens.
    var pd = document.getElementById('Product-Drawer');
    if (pd) {
      var imgs = pd.querySelector('.product-quick-images--container');
      if (imgs) imgs.classList.remove('active');
      pd.classList.remove('active');
    }
    document.body.classList.remove('open-quick-view');
  }

  function openDrawerWithFreshContents() {
    closeQuickViewPanels();
    fetch(window.location.pathname + '?sections=cart-drawer')
      .then(function (r) { return r.json(); })
      .then(function (data) {
        var drawer = document.getElementById('Cart-Drawer');
        if (drawer && data['cart-drawer']) {
          var inner = getSectionInnerHTML(data['cart-drawer'], '.cart-drawer');
          var target = drawer.querySelector('.cart-drawer') || drawer;
          if (inner) target.innerHTML = inner;
        }
        if (drawer) {
          drawer.classList.add('active');
          document.body.classList.add('open-cart');
          document.body.classList.add('open-cc');
          document.dispatchEvent(new CustomEvent('cart-drawer:open', { bubbles: true }));
        } else {
          window.location.href = '/cart';
        }
      })
      .catch(function () { window.location.href = '/cart'; });
  }

  function addToCart(variantId, quantity) {
    if (!variantId || isNaN(parseInt(variantId, 10))) {
      return Promise.reject(new Error('Could not add to cart'));
    }
    return fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify({ items: [{ id: parseInt(variantId, 10), quantity: quantity || 1 }] })
    })
      .then(function (r) {
        return r.json().then(function (data) {
          // Batch 1 #1 (2026-08-01): /cart/add.js returns 422 with a message on
          // failure (e.g. sold out). Previously swallowed — the drawer opened
          // empty. Now the failure propagates and the drawer stays closed.
          if (!r.ok) {
            throw new Error(data.description || data.message || 'Could not add to cart');
          }
          return data;
        });
      })
      .then(function (data) {
        document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
        openDrawerWithFreshContents();
        return data;
      });
  }

  function showAddError(btn, message, isCapText) {
    // Brent, 6 Oct 2026 ("match PDP"): the cap sentence goes into the product page's own message box when the block
    // renders one next to the button (kl-buy-block.liquid: .product-form__error-message-wrapper + "!" icon) — the same
    // innerHTML rule product.js uses (handleErrorMessage: HTML only for the sentence the theme itself rendered).
    var box = isCapText ? btn.parentNode.querySelector('.product-form__error-message-wrapper') : null;
    if (box) {
      box.querySelector('.product-form__error-message').innerHTML = message;
      box.removeAttribute('hidden');
      return;
    }
    var err = btn.parentNode.querySelector('.kl-add-error');
    if (!err) {
      err = document.createElement('p');
      err.className = 'kl-add-error';
      btn.insertAdjacentElement('afterend', err);
    }
    if (isCapText) { err.innerHTML = message; err.dataset.klCap = '1'; } else { err.textContent = message; delete err.dataset.klCap; }
  }

  /* Hides the CAP sentence only (HOD r2: a "sold out" line under the same button is not ours to clear). */
  function clearAddError(btn) {
    var box = btn.parentNode.querySelector('.product-form__error-message-wrapper');
    if (box) { box.setAttribute('hidden', ''); return; }
    var err = btn.parentNode.querySelector('.kl-add-error');
    if (err && err.dataset.klCap === '1') err.remove();
  }

  /* 6 Oct 2026: re-read the cap room of every cap-carrying add button from the server after the cart changes
     (the section that drew the button, rendered again — the same source as the page load). */
  var capRefreshTimer = null;
  function refreshCapRooms() {
    clearTimeout(capRefreshTimer);
    capRefreshTimer = setTimeout(function () {
      var btns = document.querySelectorAll('[data-kl-add][data-kl-cap-room][data-kl-section]');
      var sections = {};
      btns.forEach(function (b) { (sections[b.dataset.klSection] = sections[b.dataset.klSection] || []).push(b); });
      Object.keys(sections).forEach(function (sid) {
        fetch(window.location.pathname + '?section_id=' + encodeURIComponent(sid))
          .then(function (r) { return r.text(); })
          .then(function (html) {
            var doc = new DOMParser().parseFromString(html, 'text/html');
            sections[sid].forEach(function (b) {
              var fresh = b.id ? doc.getElementById(b.id) : null;
              if (!fresh || fresh.dataset.klCapRoom === undefined) return;
              b.dataset.klCapRoom = fresh.dataset.klCapRoom;
              if (fresh.dataset.klCapLimit !== undefined) b.dataset.klCapLimit = fresh.dataset.klCapLimit;
              if (fresh.dataset.klCapText !== undefined) b.dataset.klCapText = fresh.dataset.klCapText;
              syncCapMessage(b);
            });
          })
          .catch(function () {});
      });
    }, 400);
  }
  /* HOD r1 note 1 (6 Oct 2026; Brent, 21 Sep: the sentence appears EVERYWHERE there is an opportunity to add): a
     button with no room shows its sentence as soon as the page is drawn or the room drops to 0 — the product page
     does the same on arrival (product.js klCapSync). Room back = message gone. */
  function syncCapMessage(b) {
    if (b.dataset.klCapRoom === undefined) return;
    if (Number(b.dataset.klCapRoom) === 0 && b.dataset.klCapText) showAddError(b, b.dataset.klCapText, true);
    else clearAddError(b);
  }
  function syncAllCapMessages() {
    document.querySelectorAll('[data-kl-add][data-kl-cap-room]').forEach(syncCapMessage);
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', syncAllCapMessages);
  else syncAllCapMessages();
  // The slide-out cart's own edits (quantity steppers, trash) change the room too.
  document.addEventListener('line-item:change:end', refreshCapRooms);
  document.documentElement.addEventListener('cart:refresh', refreshCapRooms);

  /* 1) Return-from-sign-in flow */
  var params = new URLSearchParams(window.location.search);
  var variantId = params.get('kl_add');
  if (variantId) {
    // Strip the param right away so a refresh won't re-add.
    params.delete('kl_add');
    var clean = window.location.pathname + (params.toString() ? '?' + params.toString() : '');
    window.history.replaceState({}, document.title, clean);

    // Only proceed if the customer is actually signed in now.
    if (document.body.classList.contains('kl-customer-logged-in')) {
      // Brent, 6 Oct 2026 (HOD r1 note 3, "Go"): the automatic add respects the caps too. The page the member lands
      // on carries the room (the product form's data-kl-cap-room, or a hero button's); no room = no add, and the
      // sentence shows where that page shows it. No carrier on the page = add as before (checkout still refuses).
      var carrier = document.querySelector('product-form:not([data-sticky])[data-kl-cap-room], [data-kl-add][data-kl-cap-room]');
      if (carrier && Number(carrier.dataset.klCapRoom) === 0) {
        if (typeof carrier.klCapSync === 'function') carrier.klCapSync();
        else if (typeof carrier.handleErrorMessage === 'function') carrier.handleErrorMessage(carrier.dataset.klCapText);
        else showAddError(carrier, carrier.dataset.klCapText, true);
      } else {
        addToCart(variantId)
          .catch(function () { /* silent — member can add manually */ });
      }
    }
  }

  /* 2) Member quick-add buttons.
     Quantity: respects the theme's stepper when the button sits inside the
     product form (PDP); defaults to 1 elsewhere (hero). */
  // WIN-1L it2 (2026-08-13): CAPTURE phase — the quick-view and quick-add
  // drawer components stop click propagation inside their panels, so a
  // bubble-phase document listener never fires there (Brent's dead
  // Get-it-now clicks). Capture runs on the way down, before them.
  document.addEventListener('click', function (e) {
    var btn = e.target && e.target.closest ? e.target.closest('[data-kl-add]') : null;
    if (!btn || btn.disabled) return;
    // WIN-3S F1 (2026-08-13): an EMPTY data-kl-add means the variant is not
    // yet resolved — the hero's own capture-phase gate owns that click (B5
    // bug #9 Option 1: unresolved click opens the modal, nothing else).
    // Since it2 both listeners share document/capture, so step aside fully:
    // no preventDefault, no add attempt, no spurious error.
    if (btn.getAttribute('data-kl-add') === '') return;
    e.preventDefault();
    var qty = 1;
    var form = btn.closest('form');
    // B11 4: the quantity input moved OUT of the <form> element (it sits under
    // the size picker, associated via the form="" attribute). form.elements
    // includes attribute-associated controls; querySelector does not.
    var qtyEl = null;
    if (form) {
      qtyEl = form.elements ? form.elements['quantity'] : null;
      if (!qtyEl) qtyEl = form.querySelector('input.qty');
    }
    if (qtyEl && qtyEl.value !== undefined) {
      qty = parseInt(qtyEl.value, 10);
      if (isNaN(qty) || qty < 1) qty = 1;
    }
    // Batch 2 criterion 12: resolve the variant at CLICK time. Inside the
    // product form the theme keeps input[name="id"] current as the visitor
    // changes variants — prefer it over the server-rendered data-kl-add.
    var vid = btn.getAttribute('data-kl-add');
    var idInput = form ? form.querySelector('input[name="id"]') : null;
    if (idInput && idInput.value) vid = idInput.value;
    // 6 Oct 2026 (Brent's bug report): the cap, early. A button that carries data-kl-cap-room (the hero's buy
    // button — kl-buy-block.liquid renders kl-cart-cap-notice mode 'data' on it, the same attributes the product
    // form has) is refused here when the cart has no room for this add: the 24-shirt total cap for everyone, or a
    // Primo's Tier 1 cap, whichever sentence rode in. The checkout program is still the real gate; this makes the
    // refusal visible before the shirt ever reaches the cart (product.js klCapSync does the same on product pages).
    if (btn.dataset.klCapRoom !== undefined) {
      var room = Number(btn.dataset.klCapRoom);
      if (room === 0 || qty > room) {
        showAddError(btn, btn.dataset.klCapText || 'Could not add to cart', true);
        return;
      }
    }
    btn.disabled = true;
    addToCart(vid, qty)
      .then(function () {
        btn.disabled = false;
        // The cart just changed: ask the server for every cap-carrying button's room again (the number the page was
        // built with — kl-cart-cap-notice, mode 'data'), so the next click is judged against the new cart.
        refreshCapRooms();
      })
      .catch(function (err) {
        btn.disabled = false;
        showAddError(btn, err && err.message ? err.message : 'Could not add to cart');
      });
  }, true);

  /* B3 3.10 (one invisible change): quick-view triggers are now tabbable
     (role="button", tabindex="0" in product-card.liquid). Custom elements
     get no native key activation, so Enter/Space dispatches the click the
     theme's quick-view JS already listens for. No visual change. */
  document.addEventListener('keydown', function (e) {
    if (e.key !== 'Enter' && e.key !== ' ') return;
    var qv = e.target && e.target.closest ? e.target.closest('quick-view[role="button"]') : null;
    if (!qv) return;
    e.preventDefault();
    qv.click();
  });
})();
